import { RECURLY_KEY } from '$lib/constants';
import { Toasts } from '$lib/stores';
import type { PurchaseCreatePayload } from '$lib/types/api/graphql';

export const secureFlow = async (threeDSecureActionToken?: string) => {
  const riskPromise = await new Promise((resolve, reject) => {
    if (!threeDSecureActionToken) {
      reject({ token: undefined, message: 'No 3D Secure action token provided.' });
      return;
    }

    if (!window.recurly.configured) {
      window.recurly.configure(RECURLY_KEY);
    }
    const risk = window.recurly.Risk();
    const threeDSecure = risk.ThreeDSecure({ actionTokenId: threeDSecureActionToken });
    threeDSecure.on('token', secureToken => {
      threeDSecure.remove();
      resolve({ token: secureToken.id, message: '3D Secure token generated.' });
    });
    threeDSecure.on('error', err => {
      threeDSecure.remove();
      reject({ token: undefined, message: '3D Secure token generation failed.' });
    });
    threeDSecure.attach(document.body);
  });

  return riskPromise as Promise<{ token?: string; message?: string }>;
};

/**
 * Display a toast for common purchase create errors
 * @param errors - The errors from the purchase create mutation
 */
export const purchaseCreateErrorToast = ({
  errors,
  result,
  placement = 'right'
}: PurchaseCreatePayload & {
  placement: 'left' | 'right' | 'center';
}) => {
  if (!errors && !result) return;
  let message = 'There was an issue processing your payment, please try again.';

  if (errors?.length && errors[0].message) {
    message = errors[0].message;
  }

  if (result?.paymentGatewayResponse?.code) {
    switch (result.paymentGatewayResponse.code) {
      case 'validation_error':
        message = 'Error during payment authorisation, please check all your details and try again.';
        break;
      case 'transaction_error':
      case 'missing_billing_info':
        message =
          result.paymentGatewayResponse.customerMessage ??
          'Payment error, please check your card details and funds then try again.';
        break;
      case 'wait_required':
        message = 'There was a recent purchase from you, please wait for a minute and try again.';
        break;
    }
  }

  Toasts.push({
    kind: 'error',
    content: message,
    placement
  });
};
